import { useState } from "react";
import { CheckCircle2 } from "lucide-react";
import { useTranslation } from "react-i18next";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

import {
  useCatalogueQuery,
  useExpertiseQuery,
  useRegistrationQuery,
  useServiceRegionsQuery,
} from "../../api/hooks";
import { useSectors } from "@/features/questionnaire/api";
import type { RegistrationProfile } from "../../types";
import { ErrorState, Loading } from "../ui/states";
import { ExpertiseStep } from "./steps/ExpertiseStep";
import { OrganizationStep } from "./steps/OrganizationStep";
import { ServiceRangeStep } from "./steps/ServiceRangeStep";

// Part 1 is three steps: organization → service range → expertise. The
// declaration is a modal opened from the expertise step's submit button (parity
// with the legacy app), not a separate step. Objectives + action paths (Part 2)
// come after approval.
const STEP_KEYS = ["org", "serviceRange", "expertise"] as const;

// The step list keeps its short labels; the card is titled by what the step
// asks for. The organisation step carries the CQDD's own heading, which the
// Django step also uses as its title rather than as a subheading under it.
const STEP_TITLE_KEYS: Record<(typeof STEP_KEYS)[number], string> = {
  org: "expert.register.orgOverviewHeading",
  serviceRange: "expert.register.steps.serviceRange",
  expertise: "expert.register.expertiseHeading",
};

/** Map the server-side registration_step to the wizard's starting index. */
function resumeIndex(profile: RegistrationProfile): number {
  const step = profile.registration_step ?? 1;
  if (step >= 4) return 2;
  if (step === 3) return 1;
  return 0;
}

/**
 * Server-persisted, resumable expert self-registration wizard (parity with the
 * legacy multi-step forms). Each step saves to the API on "Continue" and the
 * flow resumes at the furthest step reached. A validated expert's edits are
 * staged for CQDD re-validation server-side (see expert/staging.py).
 */
export function RegistrationFlow({ onDone }: { onDone?: () => void }) {
  const { t } = useTranslation();
  const registration = useRegistrationQuery();
  const catalogue = useCatalogueQuery();
  const regions = useServiceRegionsQuery();
  const sectors = useSectors();
  const expertise = useExpertiseQuery();

  const [step, setStep] = useState<number | null>(null);
  const [submitted, setSubmitted] = useState(false);
  // Why the expert was sent back to the organization step, shown there.
  const [organizationNotice, setOrganizationNotice] = useState<string | null>(null);
  // The same for the service-range step (the provinces answer).
  const [serviceRangeNotice, setServiceRangeNotice] = useState<string | null>(null);

  const pending =
    registration.isPending ||
    catalogue.isPending ||
    regions.isPending ||
    sectors.isPending ||
    expertise.isPending;

  if (pending) return <Loading />;
  if (
    registration.isError ||
    !registration.data ||
    !catalogue.data ||
    !expertise.data
  ) {
    return <ErrorState message={t("expert.register.loadError")} />;
  }

  const profile = registration.data;
  const current = step ?? resumeIndex(profile);

  // The step-1 pool restricts the per-enjeu scope in step 2.
  const poolIds = {
    sectors: new Set<string>(profile.sectors_served ?? []),
    orgTypes: new Set<string>(profile.organization_types_served ?? []),
  };
  const orgTypeOptions = catalogue.data.organization_types;
  const poolSectors = (sectors.data ?? []).filter((s) => poolIds.sectors.has(s.id));
  const poolOrgTypes = orgTypeOptions.filter((o) => poolIds.orgTypes.has(o.value));

  // Part 1 finalizes on the expertise step: a draft submitting for the first
  // time, or a rejected profile correcting and resubmitting, confirms the
  // declaration in a modal and submits. Submitted/validated profiles edit in
  // place (their expertise edits stage for CQDD re-validation server-side).
  const submitMode = profile.status === "draft" || profile.status === "rejected";

  if (submitted) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <CheckCircle2 className="size-5 text-success" aria-hidden />
            {t("expert.register.submittedTitle")}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm">{t("expert.register.submittedLead")}</p>
          <p className="text-sm text-muted-foreground">
            {t("expert.register.submittedNote")}
          </p>
          {onDone && (
            <Button onClick={onDone}>{t("expert.register.goToDashboard")}</Button>
          )}
        </CardContent>
      </Card>
    );
  }

  const stepKey = STEP_KEYS[current] ?? STEP_KEYS[0];

  return (
    <div className="space-y-6">
      <header className="space-y-2">
        <h2 className="text-xl font-semibold">{t("expert.register.heading")}</h2>
        <p className="text-sm text-muted-foreground">{t("expert.register.help")}</p>
      </header>

      <ol className="flex flex-wrap gap-2" aria-label={t("expert.register.heading")}>
        {STEP_KEYS.map((key, index) => (
          <li key={key}>
            <button
              type="button"
              aria-current={index === current ? "step" : undefined}
              onClick={() => setStep(index)}
              className={
                index === current
                  ? "rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground"
                  : "rounded-md bg-secondary px-3 py-1.5 text-sm text-secondary-foreground hover:bg-secondary/80"
              }
            >
              {index + 1}. {t(`expert.register.steps.${key}`)}
            </button>
          </li>
        ))}
      </ol>

      <Card>
        <CardHeader className="flex-row items-center justify-between space-y-0">
          <CardTitle className="text-lg sm:text-2xl">
            {t(STEP_TITLE_KEYS[stepKey])}
          </CardTitle>
          <span className="text-sm text-muted-foreground">
            {t("expert.register.stepLabel", {
              current: current + 1,
              total: STEP_KEYS.length,
            })}
          </span>
        </CardHeader>
        <CardContent>
          {current === 0 && organizationNotice && (
            <p role="alert" className="mb-4 text-sm font-medium text-destructive">
              {organizationNotice}
            </p>
          )}
          {current === 0 && (
            <OrganizationStep
              profile={profile}
              serviceTypes={catalogue.data.service_types}
              territories={catalogue.data.territories}
              onSaved={(next) => {
                // The fields it asked for are saved, so the notice has done its job.
                setOrganizationNotice(null);
                setStep(next);
              }}
            />
          )}
          {current === 1 && serviceRangeNotice && (
            <p role="alert" className="mb-4 text-sm font-medium text-destructive">
              {serviceRangeNotice}
            </p>
          )}
          {current === 1 && (
            <ServiceRangeStep
              profile={profile}
              regions={regions.data ?? []}
              sectors={sectors.data ?? []}
              orgTypes={orgTypeOptions}
              otherProvinces={catalogue.data.other_provinces}
              onSaved={() => {
                setServiceRangeNotice(null);
                setStep(2);
              }}
              onBack={() => setStep(0)}
            />
          )}
          {current === 2 && (
            <ExpertiseStep
              catalogue={catalogue.data.thematics}
              expertiseState={expertise.data}
              poolSectors={poolSectors}
              poolOrgTypes={poolOrgTypes}
              preapproved={new Set(expertise.data.preapproved_issue_ids)}
              profile={profile}
              submitMode={submitMode}
              onSubmitted={() => {
                // A first-time draft submission shows the one-time confirmation
                // card (parity with the legacy registration_done page). A
                // rejected resubmit or an in-place edit returns to the dashboard.
                if (profile.status === "draft") setSubmitted(true);
                else onDone?.();
              }}
              onBack={() => setStep(1)}
              onOrganizationIncomplete={(message) => {
                setOrganizationNotice(message);
                setStep(0);
              }}
              onServiceRangeIncomplete={(message) => {
                setServiceRangeNotice(message);
                setStep(1);
              }}
            />
          )}
        </CardContent>
      </Card>
    </div>
  );
}
